'use client'

import { useState } from 'react'
import { signIn } from 'next-auth/react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { storeRefCode } from '@/lib/referral/ref-code'

type Props = {
  callbackUrl?: string
  label?: string
  /** Referral code from ?ref= URL param — stashed to a cookie before the
   *  Google OAuth redirect so it survives the round-trip. */
  referralCode?: string
}

export function SignInButton({
  callbackUrl = '/dashboard',
  label = 'Continue with Google',
  referralCode,
}: Props) {
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  async function handleSignIn() {
    // (b) Stash the ref code immediately before the OAuth redirect.
    // Belt-and-suspenders: the signup page also writes it on mount via
    // useEffect, but this guarantees the cookie exists even if mount
    // and click race on slow devices.
    if (referralCode) {
      storeRefCode(referralCode)
    }
    const DEBUG = process.env.NODE_ENV === 'development' // dev-only debug logs
    if (DEBUG) {
      console.log('[SignInButton] Click: starting signIn(google)', { callbackUrl })
      console.log(
        '[SignInButton] Current origin:',
        typeof window !== 'undefined' ? window.location.origin : 'SSR',
      )
    }

    setLoading(true)
    try {
      if (DEBUG)
        console.log("[SignInButton] Calling signIn('google', { callbackUrl, redirect: true })...")
      const result = await signIn('google', { callbackUrl, redirect: true })
      if (DEBUG) console.log('[SignInButton] signIn() returned (no redirect happened):', result)

      // If we get here without redirecting, there was an error
      if (result?.error) {
        if (DEBUG)
          console.error(
            '[SignInButton] signIn error:',
            result.error,
            'status:',
            result.status,
            'url:',
            result.url,
          )
        toast({
          title: 'Sign-in failed',
          description:
            result.error === 'Configuration'
              ? 'Sign-in is not configured. Check server logs.'
              : result.error,
          variant: 'destructive',
        })
      } else if (DEBUG) {
        console.log(
          "[SignInButton] No error in result but we didn't redirect – check NEXTAUTH_URL and Google redirect URI.",
        )
      }
    } catch (err) {
      if (DEBUG) {
        console.error('[SignInButton] signIn threw:', err)
        console.error(
          '[SignInButton] err stringified:',
          JSON.stringify(err, Object.getOwnPropertyNames(err)),
        )
      }
      toast({
        title: 'Sign-in failed',
        description: err instanceof Error ? err.message : 'Something went wrong',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
      if (DEBUG) console.log('[SignInButton] handleSignIn finished.')
    }
  }

  return (
    <Button
      type="button"
      className="w-full bg-brand text-on-brand"
      onClick={handleSignIn}
      disabled={loading}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
    </Button>
  )
}
